import React, { useState, useEffect } from "react";
import { dataService } from "../../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
// import admin from "firebase-admin";
import { Factory, Repeat, X, Plus, Save, Trash2, History } from "lucide-react";
import { crudTypes } from "../../../../../Config/constants";
// components
import CustomCheckbox from "../../../../Shared/Checkbox";
import Button from "../../../../Shared/Buttons/Button";
import Textarea from "../../../../Shared/Textarea";
import Chip from "../../../../Shared/ui/Chip";
import Input from "../../../../Shared/Input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectGroup, SelectLabel } from "../../../../Shared/Select";
import Divider from "../../../../Shared/ui/Divider";

export default function PriceDefinitionDialog({ dialogOpen, closeDialog, priceDefinition, crudType, ...props }) {
  const classes = useStyles();

  const [generalError, setGeneralError] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);

  const [schedulingPriceChange, setSchedulingPriceChange] = useState(false);

  // Data
  const [allCompanies, setAllCompanies] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyError, setSelectedCompanyError] = useState(null);

  const [code, setCode] = useState(null);
  const [codeError, setCodeError] = useState(null);

  const [name, setName] = useState(null);
  const [nameError, setNameError] = useState(null);

  const [description, setDescription] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);

  const [price, setPrice] = useState(null);
  const [priceError, setPriceError] = useState(null);

  const [uom, setUom] = useState(null);
  const [uomError, setUomError] = useState(null);

  const [recurrencePeriod, setRecurrencePeriod] = useState(null);
  const [recurrencePeriodError, setRecurrencePeriodError] = useState(null);

  const [allPriceEntries, setAllPriceEntries] = useState([]);

  const [createAnotherDefinition, setCreateAnotherDefinition] = useState(false);

  const [scheduledPrice, setScheduledPrice] = useState("");
  const [scheduledPriceError, setScheduledPriceError] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(new Date());

  // Confirmation state for deleting price entries
  const [confirmDelete, setConfirmDelete] = useState(null); // stores the priceChangeId to delete

  // Confirmation state for deleting the entire price definition
  const [confirmDeleteDefinition, setConfirmDeleteDefinition] = useState(false);

  // History view state
  const [showingHistory, setShowingHistory] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (props.Login && props.Login.userid) {
      // Get all companies
      dataService
        .getAllCompaniesByUserId(props.Login.userid)
        .then((response) => {
          setAllCompanies(response);
          if (response.length === 1) {
            setSelectedCompany(response[0]);
          }
        })
        .catch((error) => {
          console.error("Error fetching companies:", error);
          setGeneralError("An error occurred while fetching companies. Please try again later.");
        });
    }
  }, [props.Login.userid]);

  useEffect(() => {
    resetErrors();
    resetFields();
    if (crudType === crudTypes.CREATE) {
      console.log("Creating new price definition");
    } else if (crudType === crudTypes.UPDATE) {
      if (priceDefinition) {
        setSelectedCompany(allCompanies.find((company) => company.id === priceDefinition.companyId));
        setCode(priceDefinition.code);
        setName(priceDefinition.name);
        setDescription(priceDefinition.description);
        const currentPrice = priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.price;
        if (currentPrice) {
          // Format price with 3 decimals and European formatting for display in input
          const formattedPrice = currentPrice.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1");
          setPrice(formattedPrice);
        } else {
          setPrice(null);
        }
        setUom(priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.unitOfMeasurement || null);
      }
    } else if (crudType === crudTypes.READ) {
      if (priceDefinition) {
        console.log("Reading price definition", priceDefinition);
        setSelectedCompany(allCompanies.find((company) => company.id === priceDefinition.companyId));
        setCode(priceDefinition.code);
        setName(priceDefinition.name);
        setDescription(priceDefinition.description);
        const currentPrice = priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.price;
        if (currentPrice) {
          // Format price with 3 decimals and European formatting for display in input
          const formattedPrice = currentPrice.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1");
          setPrice(formattedPrice);
        } else {
          setPrice(null);
        }
        setUom(priceDefinition.entries.find((entry) => entry.isCurrentPrice)?.unitOfMeasurement || null);
        setAllPriceEntries(priceDefinition.entries || []);
      }
    } else {
      setDisclaimer("What are you gonna do?");
    }
  }, [crudType, priceDefinition, allCompanies]);

  const getDialogTitle = () => {
    switch (crudType) {
      case crudTypes.CREATE:
        return "Create Price Definition";
      case crudTypes.UPDATE:
        return "Edit" + (priceDefinition ? ` ${priceDefinition.name}` : "");
      case crudTypes.READ:
        return priceDefinition ? `${priceDefinition.code} - ${priceDefinition.name}` : "Price Definition";
      default:
        return "Subscriber";
    }
  };

  const getSubmitLabel = () => {
    switch (crudType) {
      case crudTypes.CREATE:
        return "Create Price Definition";
      case crudTypes.UPDATE:
        return "Save Changes";
      default:
        return "Submit";
    }
  };

  const changeSelectedCompany = (company) => {
    setSelectedCompany(company);
  };

  const changeCode = (e) => {
    setCode(e.target.value);
    if (e.target.value.length < 1) {
      setCode(null);
    }
  };

  const changeName = (e) => {
    setName(e.target.value);
    if (e.target.value.length < 1) {
      setName(null);
    }
  };

  const changeDescription = (e) => {
    setDescription(e.target.value);
    if (e.target.value.length < 1) {
      setDescription(null);
    }
  };

  const changePrice = (e) => {
    setPrice(e.target.value);
    if (e.target.value.length < 1) {
      setPrice(null);
    }
  };

  const changeUom = (e) => {
    setUom(e.target.value);
    if (e.target.value.length < 1) {
      setUom(null);
    }
  };

  const resetErrors = () => {
    setNameError(null);
    setCodeError(null);
    setDescriptionError(null);
    setPriceError(null);
    setUomError(null);
    setRecurrencePeriodError(null);
    setSelectedCompanyError(null);
    setGeneralError(null);
  };

  const resetFields = () => {
    setName("");
    setSelectedCompany(null);
    setCode("");
    setDescription("");
    setPrice(null);
    setUom("");
    setRecurrencePeriod(null);
    setCreateAnotherDefinition(false);
    setDisclaimer(null);
  };

  const resetFieldsKeepCompany = () => {
    setName("");
    setCode("");
    setDescription("");
    setPrice(null);
    setUom("");
    setRecurrencePeriod(null);
    setDisclaimer(null);
    // Keep selectedCompany and createAnotherDefinition unchanged
  };

  const handleCloseDialog = () => {
    setSchedulingPriceChange(false);
    setConfirmDeleteDefinition(false);
    setShowingHistory(false);
    setHistoryEntries([]);
    resetErrors();
    resetFields();
    closeDialog();
  };

  const handleToggleHistory = () => {
    if (showingHistory) {
      setShowingHistory(false);
      return;
    }
    if (!priceDefinition) return;
    setHistoryLoading(true);
    dataService
      .getPriceHistory(priceDefinition.id)
      .then((entries) => {
        setHistoryEntries(entries || []);
        setShowingHistory(true);
      })
      .catch(() => {
        setGeneralError("An error occurred while fetching price history.");
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  };

  const handleSubmit = async () => {
    switch (crudType) {
      case crudTypes.CREATE:
        handleCreatePriceDefinition();
        break;
      case crudTypes.UPDATE:
        handleUpdatePriceDefinition();
        break;
      default:
        setGeneralError("An error occurred while processing your request. Please try again later.");
        return;
    }
  };

  const handleCreatePriceDefinition = async () => {
    const isValid = validateCreateFields();

    if (!isValid) {
      return;
    }
    // dataservice
    const newPriceDefinition = {
      code: code,
      name: name,
      description: description,
      companyId: selectedCompany.id,
      currencyId: selectedCompany.currencyId,
      price: parseFloat(price.replace(/\./g, "").replace(/,/g, ".")), // Convert European format to decimal
      unitOfMeasurement: uom,
      insertedByUserId: props.Login.userid,
    };

    dataService
      .createPriceDefinition(newPriceDefinition)
      .then((response) => {
        if (response.success) {
          toast.success("Price definition created successfully.");
          if (props.onAdd) {
            props.onAdd(response.priceDefinition);
          }
          if (createAnotherDefinition) {
            resetErrors();
            resetFieldsKeepCompany();
          } else {
            handleCloseDialog();
          }
        } else {
          setGeneralError(response.message || "An error occurred while creating the price definition. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while creating the price definition. Please try again later.");
      });
  };

  const handleUpdatePriceDefinition = async () => {
    const isValid = validateUpdateFields();
    if (!isValid) {
      return;
    }
    // dataservice
    const updatedPriceDefinition = {
      id: priceDefinition.id,
      code: code,
      name: name,
      description: description,
      currencyId: selectedCompany.currencyId,
      price: parseFloat(price.replace(/\./g, "").replace(/,/g, ".")), // Convert European format to decimal
      unitOfMeasurement: uom,
      updatedByUserId: props.Login.userid,
    };
    dataService
      .updatePriceDefinition(updatedPriceDefinition)
      .then((response) => {
        if (response.success) {
          toast.success("Price definition updated successfully.");
          if (props.onUpdate) {
            props.onUpdate(response.priceDefinition);
          }
          handleCloseDialog();
        } else {
          setGeneralError(response.message || "An error occurred while updating the price definition. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while updating the price definition. Please try again later.");
      });
  };

  const handleSchedulePriceChange = () => {
    setGeneralError(null);
    // Convert datetime-local format to Taplr format (day/month/year)
    const formatDateForBackend = (datetimeLocalValue) => {
      const date = new Date(datetimeLocalValue);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const newPriceChange = {
      priceDefinitionId: priceDefinition.id,
      price: parseFloat(scheduledPrice.replace(/\./g, "").replace(/,/g, ".")), // Convert European format to decimal
      validFrom: formatDateForBackend(effectiveDate),
      insertedByUserId: props.Login.userid,
    };
    dataService
      .schedulePriceChange(newPriceChange)
      .then((response) => {
        if (response.success) {
          // Sort entries: current price first, then by ValidFrom date (earliest first)
          const sortedEntries = (response.priceDefinition.entries || []).sort((a, b) => {
            // Current price always first
            if (a.isCurrentPrice && !b.isCurrentPrice) return -1;
            if (!a.isCurrentPrice && b.isCurrentPrice) return 1;
            
            // If both are current or both are not current, sort by validFrom
            const dateA = new Date(a.validFrom || 0);
            const dateB = new Date(b.validFrom || 0);
            return dateA - dateB;
          });
          
          setAllPriceEntries(sortedEntries);
          if (props.onUpdate) {
            props.onUpdate(response.priceDefinition);
          }
          setSchedulingPriceChange(false);
          setScheduledPrice("");
          setEffectiveDate(new Date());
        } else {
          setGeneralError(response.message || "An error occurred while scheduling the price change. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while scheduling the price change. Please try again later.");
      })
      .finally(() => {});
  };

  const handleDeletePriceChange = (priceChangeId) => {
    dataService
      .deletePriceChange(priceChangeId)
      .then((response) => {
        if (response.success) {
          toast.success("Price change deleted successfully.");
          setAllPriceEntries((prevEntries) => prevEntries.filter((entry) => entry.id !== priceChangeId));
          props.onUpdate(response.priceDefinition);
        } else {
          setGeneralError(response.message || "An error occurred while deleting the price change. Please try again later.");
          return;
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while deleting the price change. Please try again later.");
      })
      .finally(() => {
        setConfirmDelete(null); // Clear confirmation state
      });
  };

  const handleConfirmDelete = (priceChangeId) => {
    setConfirmDelete(priceChangeId);
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  const handleDeletePriceDefinition = () => {
    dataService
      .deletePriceDefinition(priceDefinition.id)
      .then((response) => {
        if (response.success) {
          toast.success("Price definition deleted successfully.");
          if (props.onDelete) {
            props.onDelete(priceDefinition.id);
          }
          setConfirmDeleteDefinition(false);
          handleCloseDialog();
        } else {
          setGeneralError(response.message || "An error occurred while deleting the price definition. Please try again later.");
          setConfirmDeleteDefinition(false);
        }
      })
      .catch((error) => {
        setGeneralError("An error occurred while deleting the price definition. Please try again later.");
        setConfirmDeleteDefinition(false);
      });
  };

  const validateCreateFields = () => {
    let isValid = true;
    resetErrors();
    if (!selectedCompany) {
      setSelectedCompanyError("Required");
      isValid = false;
    }
    if (!code || code.length < 1) {
      setCodeError("Required");
      isValid = false;
    }
    if (!name || name.length < 1) {
      setNameError("Required");
      isValid = false;
    }
    if (!price || price.length < 1) {
      setPriceError("Required");
      isValid = false;
    }
    if (!uom || uom.length < 1) {
      setUomError("Required");
      isValid = false;
    }
    return isValid;
  };

  const validateUpdateFields = () => {
    let isValid = true;
    resetErrors();
    if (!code || code.length < 1) {
      setCodeError("Required");
      isValid = false;
    }
    if (!name || name.length < 1) {
      setNameError("Required");
      isValid = false;
    }
    if (!price || price.length < 1) {
      setPriceError("Required");
      isValid = false;
    }
    if (!uom || uom.length < 1) {
      setUomError("Required");
      isValid = false;
    }
    return isValid;
  };

  // Price Entry component
  const PriceEntry = ({ entry, previousEntry }) => {
    const calculatePercentageChange = () => {
      if (!previousEntry || previousEntry.price === 0) return null;
      const change = ((entry.price - previousEntry.price) / previousEntry.price) * 100;
      return change;
    };

    const percentageChange = calculatePercentageChange();

    const formatPrice = (priceEntry) => {
      if (priceEntry) {
        return `${priceEntry.currencySymbol}${priceEntry.price
          .toFixed(3)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
          .replace(/\.(\d{3})$/, ",$1")}`;
      } else {
        return "N/A";
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return (
        date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }) +
        " at " +
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );
    };

    return (
      <div>
        {!entry.isCurrentPrice && entry.validFrom && <p className={classes.validFromDate}>{formatDate(entry.validFrom)}</p>}
        <div className={classes.priceEntry}>
          <div className={classes.priceInfo}>
            <p className={classes.price}>
              {formatPrice(entry) + "/" + entry.unitOfMeasurement}
              {!entry.isCurrentPrice && percentageChange !== null && (
                <Chip variant={percentageChange >= 0 ? "success" : "danger"} size="small" style={{ marginLeft: "8px" }}>
                  {percentageChange >= 0 ? "+" : ""}
                  {percentageChange.toFixed(1)}%
                </Chip>
              )}
            </p>
          </div>
          {entry.isCurrentPrice && <Chip variant="success">Active Price</Chip>}
          {!entry.isCurrentPrice && (
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              {confirmDelete === entry.id ? (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <div className={classes.slideInFromRight}>
                    <Button variant="destructive" size="small" onClick={() => handleDeletePriceChange(entry.id)}>
                      Confirm Delete
                    </Button>
                  </div>
                  <div className={classes.fadeIn}>
                    <Button iconButton variant="secondary" size="small" onClick={handleCancelDelete} startIcon={<X />} />
                  </div>
                </div>
              ) : (
                <div className={confirmDelete ? classes.slideOutToRight : ""}>
                  <Button iconButton variant="secondary" size="small" onClick={() => handleConfirmDelete(entry.id)} startIcon={<Trash2 />} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          {showingHistory ? "Price History" : getDialogTitle()}
        </DialogTitle>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {crudType === crudTypes.READ && priceDefinition && (
            <button onClick={handleToggleHistory} className={`${classes.dialog__historyButton} ${showingHistory ? classes.dialog__historyButtonActive : ""}`} title="Price history">
              <History size={17} />
            </button>
          )}
          <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
            <X size={19} />
          </button>
        </div>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {generalError && (
            <div className={classes.errorContainer}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          {(crudType === crudTypes.UPDATE || (crudType === crudTypes.READ && !showingHistory)) && (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: "8px", width: "100%", marginBottom: "16px" }}>
              {selectedCompany && (
                <Chip variant="light" startAdornment={<Factory />}>
                  {selectedCompany.name}
                </Chip>
              )}
            </div>
          )}
          {/* --- History View --- */}
          {crudType === crudTypes.READ && showingHistory && (
            <div className={classes.priceEntries}>
              {historyLoading ? (
                <p style={{ textAlign: "center", color: "#666", fontSize: "14px" }}>Loading history...</p>
              ) : historyEntries.length === 0 ? (
                <p style={{ textAlign: "center", color: "#666", fontSize: "14px" }}>No price history available.</p>
              ) : (
                historyEntries.map((entry, index) => {
                  const previousEntry = index > 0 ? historyEntries[index - 1] : null;
                  const percentageChange = previousEntry && previousEntry.price !== 0
                    ? ((entry.price - previousEntry.price) / previousEntry.price) * 100
                    : null;

                  const formatHistoryPrice = (e) => {
                    return `${e.currencySymbol}${e.price.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{3})$/, ",$1")}`;
                  };

                  const formatHistoryDate = (dateString) => {
                    if (!dateString) return "";
                    const date = new Date(dateString);
                    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) +
                      " at " +
                      date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
                  };

                  return (
                    <div key={entry.id}>
                      <p className={classes.validFromDate}>{formatHistoryDate(entry.validFrom)}</p>
                      <div className={classes.priceEntry}>
                        <div className={classes.priceInfo}>
                          <p className={classes.price}>
                            {formatHistoryPrice(entry) + "/" + entry.unitOfMeasurement}
                            {percentageChange !== null && (
                              <Chip variant={percentageChange >= 0 ? "success" : "danger"} size="small" style={{ marginLeft: "8px" }}>
                                {percentageChange >= 0 ? "+" : ""}{percentageChange.toFixed(1)}%
                              </Chip>
                            )}
                          </p>
                        </div>
                        {entry.isCurrentPrice && <Chip variant="success">Active</Chip>}
                        {!entry.isCurrentPrice && new Date(entry.validTo) < new Date() && (
                          <Chip variant="light" size="small">Expired</Chip>
                        )}
                        {!entry.isCurrentPrice && new Date(entry.validFrom) > new Date() && (
                          <Chip variant="warning" size="small">Scheduled</Chip>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          {/* --- Read --- */}
          {crudType === crudTypes.READ && !showingHistory && priceDefinition && (
            <>
              {priceDefinition.description && <p className={classes.priceDefinitionDescription}>{priceDefinition.description}</p>}
              <div className={classes.priceEntries}>
                {allPriceEntries.map((entry, index) => (
                  <PriceEntry entry={entry} previousEntry={index > 0 ? allPriceEntries[index - 1] : null} index={index} key={index} />
                ))}
              </div>
              {/* --- Schedule price change --- */}
              {schedulingPriceChange && (
                <>
                  <Divider style={{ margin: "1rem 0" }}>Price change</Divider>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      padding: "8px",
                      border: "1px solid #EDEDED",
                      borderRadius: "8px",
                      background: "#F8F8F8",
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px" }}>
                      {/* <Input type="text" value={scheduledPrice} onChange={(e) => setScheduledPrice(e.target.value)} placeholder="0.00" /> */}
                      {/* <TextField
                        className={classes.textField}
                        placeholder="0,00"
                        type="text"
                        variant="outlined"
                        size="small"
                        onChange={(event) => {
                          let value = event.target.value.replace(/\./g, "");
                          // Allow only numbers and up to 3 decimals (with comma as decimal separator)
                          if (/^\d*,?\d{0,3}$/.test(value)) {
                            // Format with thousand separators (dots) and comma for decimals
                            const [intPart, decPart] = value.split(",");
                            const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                            const formattedValue = decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
                            setScheduledPrice(formattedValue);
                          }
                        }}
                        value={scheduledPrice || ""}
                        fullWidth={true}
                        error={scheduledPriceError}
                        style={{ textAlign: "right" }}
                      /> */}
                      <Input
                        placeholder="0,000"
                        type="text"
                        onChange={(event) => {
                          let value = event.target.value.replace(/\./g, "");
                          // Allow only numbers and up to 3 decimals (with comma as decimal separator)
                          if (/^\d*,?\d{0,3}$/.test(value)) {
                            // Format with thousand separators (dots) and comma for decimals
                            const [intPart, decPart] = value.split(",");
                            const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                            const formattedValue = decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
                            setScheduledPrice(formattedValue);
                          }
                        }}
                        value={scheduledPrice || ""}
                        fullWidth={true}
                        error={scheduledPriceError}
                        style={{ textAlign: "right", backgroundColor: "white" }}
                      />
                      <Input style={{ backgroundColor: "white" }} type="datetime-local" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                    </div>
                    {/* <Button iconButton variant="secondary" size="small" onClick={handleSchedulePriceChange} startIcon={<Save />} /> */}
                    <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Button variant="secondary" size="small" onClick={() => setSchedulingPriceChange(false)}>
                        Cancel
                      </Button>
                      <Button variant="primary" size="small" startIcon={<Save />} onClick={handleSchedulePriceChange}>
                        Schedule
                      </Button>
                    </div>
                  </div>
                </>
              )}
              {!schedulingPriceChange && (
                <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "16px" }}>
                  <Button variant="ghost" size="small" startIcon={<Plus />} onClick={() => setSchedulingPriceChange(true)}>
                    Schedule price change
                  </Button>
                </div>
              )}
            </>
          )}
          {/* --- Create/Update --- */}
          {(crudType === crudTypes.CREATE || crudType === crudTypes.UPDATE) && (
            <div className={classes.form}>
              {crudType === crudTypes.CREATE && (
                <>
                  <div className={classes.inputContainer}>
                    <div className={classes.labelContainer}>
                      <label className={classes.labelText}>Company*</label>
                      <p className={classes.inputError}>{selectedCompanyError}</p>
                    </div>
                    <Select value={selectedCompany?.id} onChange={changeSelectedCompany} error={selectedCompanyError !== null}>
                      <SelectTrigger>{selectedCompany?.name || "Select a company"}</SelectTrigger>
                      <SelectContent>
                        {allCompanies.map((company) => (
                          <SelectItem key={company.id} value={company}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%" }}>
                <div className={classes.inputContainer} style={{ flex: "1" }}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Code*</label>
                    <p className={classes.inputError}>{codeError}</p>
                  </div>
                  <Input type="text" onChange={(event) => changeCode(event)} value={code} fullWidth={true} error={codeError !== null} inputProps={{ maxLength: 50 }} />
                </div>
                <div className={classes.inputContainer} style={{ flex: "2" }}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Name*</label>
                    <p className={classes.inputError}>{nameError}</p>
                  </div>
                  <Input type="text" onChange={(event) => changeName(event)} value={name} fullWidth={true} error={nameError !== null} inputProps={{ maxLength: 50 }} />
                </div>
              </div>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>Description</label>
                  <p className={classes.inputError}>{descriptionError}</p>
                </div>
                <Textarea value={description} onChange={(event) => changeDescription(event)} />
              </div>
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%" }}>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Price*</label>
                    <p className={classes.inputError}>{priceError}</p>
                  </div>
                  <Input
                    placeholder="0,000"
                    type="text"
                    onChange={(event) => {
                      let value = event.target.value.replace(/\./g, "");
                      // Allow only numbers and up to 3 decimals (with comma as decimal separator)
                      if (/^\d*,?\d{0,3}$/.test(value)) {
                        // Format with thousand separators (dots) and comma for decimals
                        const [intPart, decPart] = value.split(",");
                        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                        const formattedValue = decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
                        setPrice(formattedValue);
                      }
                    }}
                    value={price || ""}
                    fullWidth={true}
                    error={priceError !== null}
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>UOM*</label>
                    <p className={classes.inputError}>{uomError}</p>
                  </div>
                  <Input type="text" onChange={(event) => changeUom(event)} value={uom} fullWidth={true} error={uomError !== null} inputProps={{ maxLength: 50 }} />
                </div>
              </div>
            </div>
          )}
          {disclaimer && (
            <div className={classes.disclaimerContainer}>
              <p className={classes.disclaimerMessage}>{disclaimer}</p>
            </div>
          )}
        </div>
      </DialogContent>
      {(crudType === crudTypes.CREATE || crudType === crudTypes.UPDATE) && (
        <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {crudType === crudTypes.UPDATE && (
              <Button iconButton variant="secondary" size="small" onClick={() => setConfirmDeleteDefinition(true)} startIcon={<Trash2 />} />
            )}
            {crudType === crudTypes.CREATE && (
              <div className={classes.inputContainer} style={{ width: "fit-content" }}>
                <CustomCheckbox checked={createAnotherDefinition} onChange={setCreateAnotherDefinition} label="Create another" />
              </div>
            )}
          </div>
          <Button onClick={() => handleSubmit()}>{getSubmitLabel()}</Button>
        </DialogActions>
      )}
    </Dialog>

    {/* Confirm Delete Dialog */}
    <Dialog open={confirmDeleteDefinition} onClose={() => setConfirmDeleteDefinition(false)} classes={{ paper: classes.confirmDialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title}>Delete Price Definition</DialogTitle>
        <button onClick={() => setConfirmDeleteDefinition(false)} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <p style={{ margin: 0, fontSize: "16px", color: "#0C0E29" }}>
          Are you sure you want to delete <strong>{priceDefinition?.name}</strong>? This action cannot be undone.
        </p>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        <Button variant="secondary" size="small" onClick={() => setConfirmDeleteDefinition(false)}>Cancel</Button>
        <Button variant="destructive" size="small" onClick={handleDeletePriceDefinition}>Delete</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

const useStyles = makeStyles(() => ({
  "@keyframes slideInFromRight": {
    "0%": {
      opacity: 0,
      transform: "translateX(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },
  "@keyframes slideOutToRight": {
    "0%": {
      opacity: 1,
      transform: "translateX(0)",
    },
    "100%": {
      opacity: 0,
      transform: "translateX(20px)",
    },
  },
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
    },
    "100%": {
      opacity: 1,
    },
  },
  slideInFromRight: {
    animation: "$slideInFromRight 0.3s ease-out",
  },
  slideOutToRight: {
    animation: "$slideOutToRight 0.2s ease-in",
  },
  fadeIn: {
    animation: "$fadeIn 0.3s ease-out",
  },
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "400px !important",
    height: "auto",
    maxHeight: "80vh !important",
    borderRadius: "6px !important",
  },

  confirmDialogPaper: {
    width: "360px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__historyButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    width: "25px",
    height: "25px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#E0E0E0",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__historyButtonActive: {
    backgroundColor: "#4654FB",
    color: "#F5F5F5",

    "&:hover": {
      backgroundColor: "#3544d9",
      color: "#F5F5F5",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  disclaimerContainer: {
    width: "100%",
    backgroundColor: "#F8F8F8",

    border: "1px solid #EDEDED",
    borderRadius: "6px",

    marginTop: "16px",
    padding: "12px",
  },

  disclaimerMessage: {
    fontSize: "16px",
    color: "#0C0E29",
  },

  priceDefinitionDescription: {
    fontSize: "16px",
    color: "#0C0E29",
    opacity: "0.8",

    margin: "0 0 1rem 0",
  },

  priceEntries: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  validFromDate: {
    margin: "0px 4px 4px 0",
    fontSize: "14px",
    color: "#666",
    textAlign: "right",
    fontWeight: "400",
  },

  priceEntry: {
    width: "100%",
    minHeight: "48px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",

    padding: "8px",

    borderRadius: "8px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #EDEDED",

    boxSizing: "border-box",

    transition: "all ease-in-out 0.2s",
  },

  priceInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
  },

  price: {
    margin: "0",
    fontSize: "16px",
    fontWeight: "500",
    color: "#0C0E29",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },

  form: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  subscriptionLimitContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputError: {
    fontSize: "14px",
    color: "#D84949",

    margin: "0",
  },

  textField: {
    height: "40px",
  },

  radioButtonContainer: {
    width: "100%",
    display: "flex",
    gap: "8px",
  },

  radioButton: {
    width: "fit-content",
    height: "40px",

    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",

    padding: "8px",

    borderRadius: "4px",

    transition: "all ease-in-out 0.2s",

    "&:hover": {
      backgroundColor: "#EDEDED",
      cursor: "pointer",
    },
  },

  radio: {
    width: "20px",
    height: "20px",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    boxSizing: "border-box",

    borderRadius: "50%",
    border: "1px solid #0C0E29",
    backgroundColor: "transparent",
  },

  radioIcon: {
    color: "#F8F8F8",
  },

  radioButton__label: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",

    margin: "0",
  },

  saveButton: {
    height: "40px",
    padding: "0 16px",

    alignSelf: "end",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#C3996B",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  deleteButton: {
    height: "40px",
    padding: "0 16px",
    verticalAlign: "center",
    border: "1px solid #D84949",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#D84949",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#C73B3B",
      color: "white",
      border: "1px solid #C73B3B",
      cursor: "pointer",
    },
  },
}));
